import React, { useEffect, useMemo, useState } from 'react';
import AppCard from '../../../../@nxsys/components/AppCard';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { useCanAccessEmployees } from '@nxsys/hooks/useCanAccessEmployees';
import { Icons } from '@nxsys/types/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '@nxsys/store';
import type { RequiredAlertsListType } from '@nxsys/types/api/analytics';
import type { RequiredAlertsCounts } from '@nxsys/store/slices/analytics/types';
import RequiredAlertsListModal from '../../modals/RequiredAlertsList';
import { getAnalyticsRequiredAlerts } from '@nxsys/store/slices/analytics/actions';
import { useDashboardEmployerAnalytics } from '../../../dashboard/context/DashboardEmployerAnalyticsContext';
import { IRELAND } from '@nxsys/store/constants/keys';

const CRITICAL_ICON_COLOR = '#CC2D30';
const DEFAULT_ICON_COLOR = '#09090B';

const getRequiredAlertsOrder = (
  isIreland: boolean,
): (keyof RequiredAlertsCounts)[] => [
  // 'onHold',
  'withRecurringPayments',
  'withRecurringDeductions',
  isIreland ? 'missingPPSNumber' : 'missingNINumber',
  'missingAddress',
  'missingEmail',
];

const ALERT_LABEL_KEYS: Record<keyof RequiredAlertsCounts, string> = {
  missingNINumber: 'common.missingNINumber',
  missingPPSNumber: 'common.missingPPSNumber',
  missingAddress: 'common.missingAddress',
  missingEmail: 'common.missingEmail',
  onHold: 'common.onHold',
  withRecurringPayments: 'common.withRecurringPayments',
  withRecurringDeductions: 'common.withRecurringDeductions',
};

const CRITICAL_ALERT_KEYS = new Set<keyof RequiredAlertsCounts>([
  'missingNINumber',
  'missingPPSNumber',
  'missingAddress',
  'missingEmail',
]);

type AlertItem = {
  id: string;
  count: number;
  label: string;
  isCritical: boolean;
  icon: React.ReactNode;
};

const getIconForAlert = (id: string, isCritical: boolean): React.ReactNode => {
  const color = isCritical ? CRITICAL_ICON_COLOR : DEFAULT_ICON_COLOR;
  const size = { width: 28, height: 28 };
  switch (id) {
    case 'missingNINumber':
    case 'missingPPSNumber':
      return <Icons.IconDocumentList {...size} color={color} />;
    case 'missingAddress':
      return <Icons.IconWarning {...size} color={color} />;
    case 'missingEmail':
      return <Icons.IconMailEdit {...size} color={color} />;
    case 'onHold':
      return <Icons.IconMailClock {...size} color={color} />;
    case 'withRecurringPayments':
      return <Icons.IconCreditCardMinus {...size} color={color} />;
    case 'withRecurringDeductions':
      return <Icons.IconMoneyBillMinus {...size} color={color} />;
    default:
      return <Icons.IconWarning {...size} color={color} />;
  }
};

const alertIdToListType = (id: string): RequiredAlertsListType | null => {
  const map: Record<string, RequiredAlertsListType> = {
    missingNINumber: 'missingNINumber',
    missingPPSNumber: 'missingPPSNumber',
    missingAddress: 'missingAddress',
    missingEmail: 'missingEmail',
    onHold: 'onHold',
    withRecurringPayments: 'withRecurringPayments',
    withRecurringDeductions: 'withRecurringDeductions',
  };
  return map[id] ?? null;
};

const ActionRequiredSection: React.FC = () => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { params } = useDashboardEmployerAnalytics();
  const { requiredAlertsData } = useSelector(
    (state: AppState) => state.analyticsStore,
  );
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  // The alert popups drill into employee records, so a dashboard-only user
  // (no CompanyEmployees) may see the counts but cannot open the list.
  const canAccessEmployees = useCanAccessEmployees();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<AlertItem | null>(null);

  const alerts: AlertItem[] = useMemo(() => {
    const data = requiredAlertsData.data;
    if (!data) return [];
    return getRequiredAlertsOrder(isIreland).map((id) => {
      const count = data[id] ?? 0;
      const isCritical = CRITICAL_ALERT_KEYS.has(id);
      const label = messages[ALERT_LABEL_KEYS[id]] || id;
      return {
        id,
        count,
        label,
        isCritical,
        icon: getIconForAlert(id, isCritical),
      };
    });
  }, [requiredAlertsData.data, messages, isIreland]);

  const handleAlertClick = (alert: AlertItem) => {
    if (!canAccessEmployees) return;
    const listType = alertIdToListType(alert.id);
    if (listType) {
      setSelectedAlert(alert);
      setModalOpen(true);
    }
  };

  const loading = requiredAlertsData.loading;
  const listType = selectedAlert ? alertIdToListType(selectedAlert.id) : null;

  useEffect(() => {
    dispatch(getAnalyticsRequiredAlerts(params));
  }, [
    dispatch,
    params?.dateFrom,
    params?.dateTo,
    params?.payFrequencies,
    params?.employerIds,
  ]);

  return (
    <>
      <AppCard
        title={messages['common.actionRequired'] || 'Action Required/ Alerts'}
      >
        <div className="px-6 pt-4 pb-6">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-600">
              {messages['common.loading'] || 'Loading...'}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {alerts.length === 0 ? (
                <div className="py-8 text-center text-gray-600">
                  {messages['common.noDataToShow'] || 'No data to show'}
                </div>
              ) : (
                alerts.map((alert) => (
                  <div
                    key={alert.id}
                    onClick={() => handleAlertClick(alert)}
                    className={`flex items-center justify-between py-2 px-4 rounded-lg ${
                      canAccessEmployees
                        ? 'cursor-pointer transition-shadow hover:shadow-md'
                        : ''
                    } ${alert.isCritical ? 'bg-red-50' : 'bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-1 flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="flex-shrink-0">{alert.icon}</div>
                        <span
                          className={`text-[28px] font-bold ${
                            alert.isCritical ? 'text-red-600' : 'text-gray-900'
                          }`}
                        >
                          {alert.count}
                        </span>
                      </div>
                      <span className="text-sm text-gray-900 truncate">
                        {alert.label}
                      </span>
                    </div>
                    {canAccessEmployees && (
                      <span className="text-gray-900 flex-shrink-0 ml-2">→</span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </AppCard>

      {listType && selectedAlert && (
        <RequiredAlertsListModal
          type={listType}
          title={selectedAlert.label}
          isOpen={modalOpen}
          toggle={() => {
            setModalOpen(false);
            setSelectedAlert(null);
          }}
          onChanged={() => dispatch(getAnalyticsRequiredAlerts(params))}
        />
      )}
    </>
  );
};

export default ActionRequiredSection;
